import { cn } from '@/lib/utils';

const STAGES = [
  'collect',
  'verify',
  'diagnose',
  'patch',
  'replay',
  'judge',
  'decide',
  'report',
  'done',
] as const;

interface StageTimelineProps {
  currentStage: string;
  status: string;
  progress: {
    stage: string;
    current: number;
    total: number;
    message: string;
    updatedAt: string;
  } | null;
  configLocked?: boolean;
}

const stageIndex = (stage: string) => STAGES.indexOf(stage as (typeof STAGES)[number]);

export const StageTimeline = ({
  currentStage,
  status,
  progress,
  configLocked,
}: StageTimelineProps) => {
  const activeIdx = stageIndex(currentStage);
  const isFailed = status === 'FAILED';
  const isCancelled = status === 'CANCELLED';

  return (
    <div>
      <div className="flex w-full items-center gap-1">
        {STAGES.map((stage, idx) => {
          const isDone = idx < activeIdx || status === 'SUCCEEDED';
          const isActive = idx === activeIdx && !isFailed && !isCancelled && status !== 'SUCCEEDED';
          const isFailing = idx === activeIdx && isFailed;

          return (
            <div key={stage} className="flex min-w-0 flex-1 flex-col items-center">
              <div
                className={cn(
                  'flex h-6 w-6 items-center justify-center rounded-full border-2 text-xs font-medium transition-colors',
                  isDone && 'border-green-500 bg-green-500 text-white',
                  isActive && 'animate-pulse border-blue-500 bg-blue-500 text-white',
                  isFailing && 'border-red-500 bg-red-500 text-white',
                  !isDone && !isActive && !isFailing && 'border-gray-300 text-gray-400',
                )}
              >
                {isDone ? '✓' : idx + 1}
              </div>
              <span
                className={cn(
                  'mt-1 max-w-full truncate text-[10px]',
                  isDone || isActive ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {stage}
              </span>
              {isActive && progress && progress.stage === stage && (
                <span className="max-w-full truncate text-[9px] text-blue-600 dark:text-blue-400">
                  {progress.current}/{progress.total}
                </span>
              )}
            </div>
          );
        })}
      </div>
      {configLocked && (
        <p className="mt-2 text-xs font-medium text-amber-600 dark:text-amber-400">
          Configuration is locked; patching was skipped
        </p>
      )}
    </div>
  );
};
